import React, { useEffect, useRef, useState } from "react";

function getPointerPosition(event, canvas) {
  const rect = canvas.getBoundingClientRect();
  const scaleX = rect.width > 0 ? canvas.width / rect.width : 1;
  const scaleY = rect.height > 0 ? canvas.height / rect.height : 1;
  return {
    x: (event.clientX - rect.left) * scaleX,
    y: (event.clientY - rect.top) * scaleY,
  };
}

function clearCanvas(canvas) {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
}

function drawLoadedImage(canvas, value) {
  if (!value) {
    clearCanvas(canvas);
    return;
  }

  const image = new Image();
  image.onload = () => {
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    clearCanvas(canvas);
    const padding = 12;
    const targetWidth = canvas.width - padding * 2;
    const targetHeight = canvas.height - padding * 2;
    const scale = Math.min(targetWidth / image.width, targetHeight / image.height, 1);
    const width = image.width * scale;
    const height = image.height * scale;
    const x = (canvas.width - width) / 2;
    const y = (canvas.height - height) / 2;
    ctx.drawImage(image, x, y, width, height);
  };
  image.src = value;
}

export default function SignaturePadField({
  id,
  value,
  onChange,
  disabled = false,
  error = "",
}) {
  const wrapperRef = useRef(null);
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef(null);
  const valueRef = useRef("");
  const [hasInk, setHasInk] = useState(Boolean(value));

  useEffect(() => {
    valueRef.current = String(value || "");
    setHasInk(Boolean(value));
  }, [value]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrapper = wrapperRef.current;
    if (!canvas || !wrapper) return undefined;

    function resizeCanvas() {
      const width = Math.max(Math.floor(wrapper.clientWidth), 280);
      const height = 180;
      canvas.width = width * 2;
      canvas.height = height * 2;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.lineWidth = 5;
      ctx.strokeStyle = "#111827";
      drawLoadedImage(canvas, valueRef.current);
    }

    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);
    return () => {
      window.removeEventListener("resize", resizeCanvas);
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    drawLoadedImage(canvas, value);
  }, [value]);

  function commitSignature() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dataUrl = canvas.toDataURL("image/png");
    onChange?.(dataUrl);
    setHasInk(true);
  }

  function startDrawing(event) {
    if (disabled) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    drawingRef.current = true;
    canvas.setPointerCapture?.(event.pointerId);
    lastPointRef.current = getPointerPosition(event, canvas);

    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const { x, y } = lastPointRef.current;
    ctx.beginPath();
    ctx.arc(x, y, 1.2, 0, Math.PI * 2);
    ctx.fillStyle = "#111827";
    ctx.fill();
  }

  function continueDrawing(event) {
    if (!drawingRef.current || disabled) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const nextPoint = getPointerPosition(event, canvas);
    const ctx = canvas.getContext("2d");
    if (!ctx || !lastPointRef.current) return;

    ctx.beginPath();
    ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y);
    ctx.lineTo(nextPoint.x, nextPoint.y);
    ctx.stroke();
    lastPointRef.current = nextPoint;
  }

  function stopDrawing(event) {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    lastPointRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    commitSignature();
  }

  function handleClear() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    clearCanvas(canvas);
    setHasInk(false);
    onChange?.("");
  }

  return (
    <div ref={wrapperRef}>
      <div className="overflow-hidden rounded-xl border border-slate-300 bg-white">
        <canvas
          id={id}
          ref={canvasRef}
          onPointerDown={startDrawing}
          onPointerMove={continueDrawing}
          onPointerUp={stopDrawing}
          onPointerLeave={stopDrawing}
          onPointerCancel={stopDrawing}
          className={`block w-full touch-none bg-white ${disabled ? "cursor-not-allowed opacity-70" : "cursor-crosshair"}`}
        />
      </div>
      <div className="mt-2 flex items-center justify-between gap-3">
        <p className="text-xs text-slate-500">
          Teken met vinger, muis of stylus. De handtekening wordt als PNG opgeslagen.
        </p>
        <button
          type="button"
          onClick={handleClear}
          disabled={disabled || !hasInk}
          className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Wissen
        </button>
      </div>
      {error ? <p className="mt-2 text-sm text-red-600">{error}</p> : null}
    </div>
  );
}
